/**
 * /admin/blog — manage Agency Blog posts.
 *
 * Lists all posts (regardless of status), and provides Create + Edit
 * modals. Publishing a post triggers the backend IndexNow ping + sitemap
 * regeneration automatically; the admin UI only needs to send a clean
 * payload.
 */

import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
    LogOut,
    Plus,
    PencilLine,
    Trash2,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Eye,
    EyeOff,
    Star,
    RefreshCw,
    Mail,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import BlogNotifyModal from "@/components/admin/BlogNotifyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
];

const CATEGORY_OPTIONS = [
    { value: "health", label: "Health" },
    { value: "personal", label: "Personal" },
    { value: "business", label: "Business" },
    { value: "special-risk", label: "Special Risk" },
];

const STATUS_STYLE = {
    draft: "border-slate-500/50 bg-slate-500/10 text-slate-100",
    published: "border-emerald-500/50 bg-emerald-500/10 text-emerald-100",
    archived: "border-rose-500/50 bg-rose-500/10 text-rose-100",
};

function slugify(s) {
    return (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function PostEditor({ post, onClose, onSaved }) {
    const isCreate = !post;
    const [form, setForm] = useState({
        title: post?.title || "",
        slug: post?.slug || "",
        summary: post?.summary || "",
        content_html: post?.content_html || "<p></p>",
        author: post?.author || "Endurance Casualty",
        hero_image_url: post?.hero_image_url || "",
        seo_title: post?.seo_title || "",
        seo_description: post?.seo_description || "",
        category: post?.category || "business",
        is_sticky: Boolean(post?.is_sticky || false),
        faqs:
            Array.isArray(post?.faqs) && post.faqs.length
                ? post.faqs.map((f) => ({
                      question: f.question || "",
                      answer: f.answer || "",
                  }))
                : [],
        status: post?.status || "draft",
        published_at: post?.published_at || "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    function change(field) {
        return (e) =>
            setForm((f) => ({
                ...f,
                [field]: e.target.value,
            }));
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const body = {
                title: form.title.trim(),
                summary: form.summary.trim(),
                content_html: form.content_html,
                author: form.author.trim() || "Endurance Casualty",
                hero_image_url: form.hero_image_url.trim() || null,
                seo_title: form.seo_title.trim() || null,
                seo_description: form.seo_description.trim() || null,
                category: form.category,
                is_sticky: Boolean(form.is_sticky),
                faqs: form.faqs
                    .map((f) => ({
                        question: (f.question || "").trim(),
                        answer: (f.answer || "").trim(),
                    }))
                    .filter((f) => f.question && f.answer),
                status: form.status,
            };
            if (isCreate) {
                body.slug =
                    form.slug.trim() || slugify(form.title.trim()) || undefined;
                if (form.published_at) body.published_at = form.published_at;
                await axios.post(`${API}/admin/blog/posts`, body);
            } else {
                if (form.published_at) body.published_at = form.published_at;
                await axios.put(
                    `${API}/admin/blog/posts/${post.slug}`,
                    body
                );
            }
            onSaved();
            onClose();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Could not save this post.";
            setError(String(msg));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
            data-testid="blog-editor-modal"
        >
            <form
                onSubmit={submit}
                className="my-auto w-full max-w-3xl rounded-2xl border border-[rgba(212,175,55,0.3)] bg-[#0b0e13] p-6 shadow-2xl sm:p-8"
            >
                <h2 className="font-serif text-2xl text-brand-ivory">
                    {isCreate ? "New blog post" : "Edit blog post"}
                </h2>
                {error ? (
                    <div className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
                        <AlertTriangle className="mr-1 inline h-4 w-4" />
                        {error}
                    </div>
                ) : null}

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="sm:col-span-2 block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Title *
                        </span>
                        <input
                            data-testid="blog-editor-title"
                            type="text"
                            required
                            value={form.title}
                            onChange={change("title")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Slug {isCreate ? "(auto if blank)" : "(read-only)"}
                        </span>
                        <input
                            data-testid="blog-editor-slug"
                            type="text"
                            value={form.slug}
                            onChange={change("slug")}
                            disabled={!isCreate}
                            placeholder={isCreate ? slugify(form.title) : ""}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Status
                        </span>
                        <select
                            data-testid="blog-editor-status"
                            value={form.status}
                            onChange={change("status")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        >
                            {STATUS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="sm:col-span-2 block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Category
                        </span>
                        <select
                            data-testid="blog-editor-category"
                            value={form.category}
                            onChange={change("category")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        >
                            {CATEGORY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label
                        className="sm:col-span-2 flex cursor-pointer items-center gap-3 rounded-lg border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-3"
                    >
                        <input
                            type="checkbox"
                            data-testid="blog-editor-sticky"
                            checked={Boolean(form.is_sticky)}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    is_sticky: e.target.checked,
                                }))
                            }
                            className="h-4 w-4 cursor-pointer accent-[#d4af37]"
                        />
                        <span className="flex-1">
                            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                <Star className="h-3.5 w-3.5 text-brand-gold" />
                                Pin as Featured
                            </span>
                            <span className="mt-1 block text-[11px] text-brand-muted">
                                Floats this post to the top of /blog and its
                                category page, with a Featured badge.
                            </span>
                        </span>
                    </label>

                    <label className="sm:col-span-2 block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Summary * (used as meta description fallback)
                        </span>
                        <textarea
                            data-testid="blog-editor-summary"
                            required
                            rows={2}
                            value={form.summary}
                            onChange={change("summary")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="sm:col-span-2 block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Body HTML *
                        </span>
                        <textarea
                            data-testid="blog-editor-content"
                            required
                            rows={10}
                            value={form.content_html}
                            onChange={change("content_html")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 font-mono text-xs text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                        <span className="mt-1 block text-[10px] text-brand-muted">
                            Allowed: paragraphs, headings (h2/h3), lists,
                            links, &lt;strong&gt;, &lt;em&gt;, &lt;img&gt;,
                            &lt;blockquote&gt;.
                        </span>
                    </label>

                    <div
                        data-testid="blog-editor-faqs"
                        className="sm:col-span-2 rounded-lg border border-[rgba(212,175,55,0.2)] bg-[#05070a] p-4"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                    Frequently Asked Questions (optional)
                                </p>
                                <p className="mt-1 text-[11px] text-brand-muted">
                                    Adding 4-6 Q&amp;A pairs emits FAQPage
                                    JSON-LD and Google may render them as
                                    expandable rich snippets in search results.
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="blog-editor-faq-add"
                                onClick={() =>
                                    setForm((f) => ({
                                        ...f,
                                        faqs: [
                                            ...f.faqs,
                                            { question: "", answer: "" },
                                        ],
                                    }))
                                }
                                className="inline-flex items-center gap-1 rounded-full border border-brand-gold/60 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:bg-brand-gold/15"
                            >
                                <Plus className="h-3 w-3" /> Add FAQ
                            </button>
                        </div>

                        {form.faqs.length === 0 ? (
                            <p className="mt-3 text-[11px] italic text-brand-muted">
                                No FAQs yet — click &quot;Add FAQ&quot; to add
                                a question-and-answer pair.
                            </p>
                        ) : (
                            <ul className="mt-3 space-y-3">
                                {form.faqs.map((f, i) => (
                                    <li
                                        key={i}
                                        data-testid={`blog-editor-faq-row-${i}`}
                                        className="rounded-md border border-[rgba(212,175,55,0.15)] bg-[#0b0e13] p-3"
                                    >
                                        <div className="flex items-start gap-2">
                                            <span className="mt-1 text-[10px] uppercase tracking-[0.2em] text-brand-goldSoft">
                                                #{i + 1}
                                            </span>
                                            <div className="flex-1 space-y-2">
                                                <input
                                                    type="text"
                                                    data-testid={`blog-editor-faq-question-${i}`}
                                                    placeholder="Question (e.g., How much does cyber insurance cost?)"
                                                    value={f.question}
                                                    onChange={(e) =>
                                                        setForm((s) => ({
                                                            ...s,
                                                            faqs: s.faqs.map(
                                                                (x, j) =>
                                                                    j === i
                                                                        ? {
                                                                              ...x,
                                                                              question:
                                                                                  e
                                                                                      .target
                                                                                      .value,
                                                                          }
                                                                        : x
                                                            ),
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                                                />
                                                <textarea
                                                    data-testid={`blog-editor-faq-answer-${i}`}
                                                    rows={3}
                                                    placeholder="Answer (3-6 sentences, plain prose; HTML is stripped from JSON-LD)"
                                                    value={f.answer}
                                                    onChange={(e) =>
                                                        setForm((s) => ({
                                                            ...s,
                                                            faqs: s.faqs.map(
                                                                (x, j) =>
                                                                    j === i
                                                                        ? {
                                                                              ...x,
                                                                              answer:
                                                                                  e
                                                                                      .target
                                                                                      .value,
                                                                          }
                                                                        : x
                                                            ),
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-xs text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                data-testid={`blog-editor-faq-remove-${i}`}
                                                onClick={() =>
                                                    setForm((s) => ({
                                                        ...s,
                                                        faqs: s.faqs.filter(
                                                            (_, j) => j !== i
                                                        ),
                                                    }))
                                                }
                                                title="Remove this FAQ"
                                                className="rounded-full border border-rose-500/40 p-1 text-rose-300 hover:bg-rose-500/15"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Author
                        </span>
                        <input
                            data-testid="blog-editor-author"
                            type="text"
                            value={form.author}
                            onChange={change("author")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Hero image URL
                        </span>
                        <input
                            data-testid="blog-editor-hero"
                            type="url"
                            value={form.hero_image_url}
                            onChange={change("hero_image_url")}
                            placeholder="https://… or /api/static/…"
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            SEO title (optional)
                        </span>
                        <input
                            data-testid="blog-editor-seotitle"
                            type="text"
                            value={form.seo_title}
                            onChange={change("seo_title")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            SEO description (optional)
                        </span>
                        <input
                            data-testid="blog-editor-seodesc"
                            type="text"
                            value={form.seo_description}
                            onChange={change("seo_description")}
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>

                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-goldSoft">
                            Published date (ISO 8601, optional)
                        </span>
                        <input
                            data-testid="blog-editor-publishedat"
                            type="text"
                            value={form.published_at || ""}
                            onChange={change("published_at")}
                            placeholder="2026-06-24T10:00:00Z"
                            className="mt-1 w-full rounded-md border border-[rgba(212,175,55,0.2)] bg-[#05070a] px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                        />
                    </label>
                </div>

                <div className="mt-8 flex flex-wrap justify-end gap-3">
                    <button
                        type="button"
                        data-testid="blog-editor-cancel"
                        onClick={onClose}
                        disabled={saving}
                        className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-brand-muted hover:bg-white/10"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        data-testid="blog-editor-save"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg hover:bg-brand-gold2 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="h-4 w-4" />
                                {isCreate ? "Create post" : "Save changes"}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function AdminBlogPage() {
    const { token, admin, loading: authLoading, logout } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | post
    const [notifying, setNotifying] = useState(null); // null | post-slug-string
    const [refreshFlag, setRefreshFlag] = useState(0);
    const [resyncing, setResyncing] = useState(false);
    const [resyncResult, setResyncResult] = useState("");

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        axios
            .get(`${API}/admin/blog/posts`)
            .then((res) => setPosts(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                setError(
                    err?.response?.data?.detail ||
                        "Could not load posts. Try refreshing."
                );
            })
            .finally(() => setLoading(false));
    }, [token, refreshFlag]);

    async function onDelete(slug) {
        if (!window.confirm("Delete this post permanently? This cannot be undone.")) {
            return;
        }
        try {
            await axios.delete(`${API}/admin/blog/posts/${slug}`);
            setRefreshFlag((n) => n + 1);
        } catch (err) {
            window.alert(
                err?.response?.data?.detail || "Failed to delete post."
            );
        }
    }

    async function onResyncMigrations() {
        if (
            !window.confirm(
                "Re-apply blog_content_migrations.py to the live database now? " +
                    "Edits will overwrite the matching fields on each post. " +
                    "This is idempotent — only changed fields are written."
            )
        ) {
            return;
        }
        setResyncing(true);
        setResyncResult("");
        try {
            const resp = await axios.post(
                `${API}/admin/blog/resync-migrations`
            );
            const data = resp.data || {};
            const msg =
                `Resync complete — inserted ${data.inserted ?? 0}, updated ${data.updated ?? 0}, ` +
                `total entries ${data.total_entries ?? "?"}` +
                (data.failed?.length ? `, failed ${data.failed.join(", ")}` : "");
            setResyncResult(msg);
            // Re-fetch the admin list ONLY when the migration actually
            // changed something — otherwise we waste a round-trip and
            // (in preview) trigger a webpack-dev-server reload via the
            // backend's sitemap rewrite (see resync endpoint behaviour).
            if ((data.inserted ?? 0) + (data.updated ?? 0) > 0) {
                setRefreshFlag((n) => n + 1);
            }
        } catch (err) {
            setResyncResult(
                "Resync failed: " +
                    (err?.response?.data?.detail || err.message || "unknown error")
            );
        } finally {
            setResyncing(false);
        }
    }

    if (authLoading) return null;
    if (!token) return <Navigate to="/admin/login" replace />;

    return (
        <main
            data-testid="admin-blog-page"
            className="min-h-screen bg-brand-bg text-brand-ivory"
        >
            <header className="border-b border-[rgba(212,175,55,0.18)] px-6 py-4">
                <div className="mx-auto flex max-w-6xl items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/videos"
                            data-testid="admin-link-videos"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Videos
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <span
                            data-testid="admin-link-blog"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-ivory"
                        >
                            Blog
                        </span>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/portal-users"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Portal users
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/analytics"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Analytics
                        </Link>
                        <span className="text-brand-muted">·</span>
                        <Link
                            to="/admin/integrations"
                            className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft hover:text-brand-gold"
                        >
                            Integrations
                        </Link>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-brand-muted">{admin?.email}</span>
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-muted hover:bg-white/10"
                        >
                            <LogOut className="h-3 w-3" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <section className="px-6 py-12">
                <div className="mx-auto max-w-6xl">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Agency Blog · 90-day rolling window
                            </p>
                            <h1 className="mt-2 font-serif text-3xl">
                                All posts
                            </h1>
                            <p className="mt-2 text-sm text-brand-muted">
                                Publishing a post fires IndexNow (Google +
                                Bing) and refreshes <code>sitemap.xml</code>
                                automatically.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                data-testid="admin-blog-resync-migrations"
                                onClick={onResyncMigrations}
                                disabled={resyncing}
                                title="Re-apply blog_content_migrations.py to the live database"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold/15 disabled:opacity-50"
                            >
                                {resyncing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4" />
                                )}
                                Re-sync from migrations
                            </button>
                            <button
                                type="button"
                                data-testid="admin-blog-new"
                                onClick={() => setEditing("new")}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg hover:bg-brand-gold2"
                            >
                                <Plus className="h-4 w-4" />
                                New post
                            </button>
                        </div>
                    </div>

                    {resyncResult ? (
                        <div
                            data-testid="admin-blog-resync-result"
                            className="mt-6 rounded-lg border border-brand-gold/30 bg-[rgba(212,175,55,0.08)] p-4 text-sm text-brand-ivory/90"
                        >
                            <CheckCircle2 className="mr-1 inline h-4 w-4 text-brand-gold" />
                            {resyncResult}
                        </div>
                    ) : null}

                    {error ? (
                        <div className="mt-8 rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                            <AlertTriangle className="mr-1 inline h-4 w-4" />
                            {error}
                        </div>
                    ) : null}

                    {loading ? (
                        <div className="mt-12 flex items-center gap-2 text-sm text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            Loading…
                        </div>
                    ) : posts.length === 0 ? (
                        <div
                            data-testid="admin-blog-empty"
                            className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted"
                        >
                            No posts yet. Click <strong>New post</strong> to
                            publish the first one.
                        </div>
                    ) : (
                        <ul
                            className="mt-8 divide-y divide-[rgba(212,175,55,0.12)] rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13]"
                            data-testid="admin-blog-list"
                        >
                            {posts.map((p) => (
                                <li
                                    key={p.slug}
                                    data-testid={`admin-blog-row-${p.slug}`}
                                    className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6"
                                >
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span
                                                className={`rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] ${
                                                    STATUS_STYLE[p.status] ||
                                                    STATUS_STYLE.draft
                                                }`}
                                            >
                                                {p.status === "published" ? (
                                                    <Eye className="-mt-0.5 mr-1 inline h-3 w-3" />
                                                ) : (
                                                    <EyeOff className="-mt-0.5 mr-1 inline h-3 w-3" />
                                                )}
                                                {p.status}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">
                                                /{p.slug}
                                            </span>
                                            {p.is_sticky ? (
                                                <span
                                                    data-testid={`admin-blog-sticky-${p.slug}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.12)] px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] text-brand-gold"
                                                >
                                                    <Star className="h-3 w-3 fill-current" />
                                                    Featured
                                                </span>
                                            ) : null}
                                            {p.published_at ? (
                                                <span className="text-[10px] text-brand-muted">
                                                    · published{" "}
                                                    {new Date(p.published_at)
                                                        .toISOString()
                                                        .slice(0, 10)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <h2 className="mt-2 font-serif text-lg text-brand-ivory">
                                            {p.title}
                                        </h2>
                                        <p className="mt-1 line-clamp-2 text-sm text-brand-muted">
                                            {p.summary}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {p.status === "published" ? (
                                            <a
                                                href={`/blog/${p.slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="rounded-full border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-muted hover:bg-white/10"
                                            >
                                                View
                                            </a>
                                        ) : null}
                                        {p.status === "published" ? (
                                            <button
                                                type="button"
                                                data-testid={`admin-blog-notify-${p.slug}`}
                                                onClick={() => setNotifying(p.slug)}
                                                className="inline-flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.08)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:bg-[rgba(212,175,55,0.18)] hover:text-brand-gold"
                                            >
                                                <Mail className="h-3 w-3" />
                                                Notify
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            data-testid={`admin-blog-edit-${p.slug}`}
                                            onClick={() => setEditing(p)}
                                            className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-muted hover:bg-white/10"
                                        >
                                            <PencilLine className="h-3 w-3" />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`admin-blog-delete-${p.slug}`}
                                            onClick={() => onDelete(p.slug)}
                                            className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-rose-200 hover:bg-rose-500/15"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                            Delete
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {editing === "new" ? (
                <PostEditor
                    post={null}
                    onClose={() => setEditing(null)}
                    onSaved={() => setRefreshFlag((n) => n + 1)}
                />
            ) : editing ? (
                <PostEditor
                    post={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => setRefreshFlag((n) => n + 1)}
                />
            ) : null}

            {notifying ? (
                <BlogNotifyModal
                    slug={notifying}
                    onClose={() => setNotifying(null)}
                />
            ) : null}
        </main>
    );
}
